"use client";

import { MiniPiano } from "@/components/ui/molecules/mini-piano";
import {
  QUALITY_LABEL,
  entrySiblings,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabels } from "@/hooks/use-notation";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import {
  BORDER,
  FONT_MONO,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";
import { INTERVAL_FORMULA } from "./pedagogy-panel-helpers";

type Props = {
  entry: ChordEntry;
  onPick?: (variant: ChordEntry) => void;
};

export function PedagogyPanelVariantsTab({ entry, onPick }: Props) {
  const siblings = entrySiblings(entry);

  return (
    <div>
      <p
        style={{
          fontSize: 12,
          color: TEXT_DIM,
          lineHeight: 1.6,
          marginBottom: 18,
        }}
      >
        Modifie la couleur de l'accord en changeant ses notes. Clique pour
        pratiquer cette variante.
      </p>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
      >
        {siblings.map((v) => (
          <VariantCard
            key={v.id}
            variant={v}
            isCurrent={v.id === entry.id}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}

type VariantCardProps = {
  variant: ChordEntry;
  isCurrent: boolean;
  onPick?: (variant: ChordEntry) => void;
};

function VariantCard({ variant, isCurrent, onPick }: VariantCardProps) {
  const v = variant;
  const chordLabel = useChordLabel();
  const noteLabels = useNoteLabels();
  const formula = v.intervals
    .map((iv) => {
      const f = INTERVAL_FORMULA[iv];

      return f ? `${f.degree}${f.sup ?? ""}` : `${iv}`;
    })
    .join(" - ");
  const rootPc = ROOT_PITCH_CLASS[v.root];
  const variantMidis = v.intervals.map((iv) => {
    let m = 60;
    const target = (rootPc + iv) % 12;
    while (m % 12 !== target) {
      m++;
    }

    return m;
  });

  return (
    <button
      type="button"
      onClick={() => onPick?.(v)}
      disabled={isCurrent || !onPick}
      style={{
        padding: 12,
        background: isCurrent ? VIOLET_SOFT : SURFACE_LO,
        border: `1px solid ${isCurrent ? VIOLET_GLOW : BORDER}`,
        borderRadius: 10,
        cursor: isCurrent || !onPick ? "default" : "pointer",
        position: "relative",
        textAlign: "left",
        fontFamily: "inherit",
        color: TEXT,
        transition: "background-color .15s, border-color .15s",
      }}
    >
      {isCurrent && (
        <span
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            fontSize: 8,
            fontFamily: FONT_MONO,
            color: VIOLET_BRIGHT,
            letterSpacing: "0.1em",
            padding: "2px 6px",
            borderRadius: 4,
            background: "rgba(139,92,246,0.2)",
          }}
        >
          ACTUEL
        </span>
      )}

      <div
        style={{
          fontSize: 18,
          fontWeight: 500,
          color: TEXT,
          letterSpacing: "-0.01em",
          marginBottom: 6,
        }}
      >
        {chordLabel(v.symbol)}
      </div>
      <div
        style={{
          fontSize: 10,
          color: TEXT_MUTE,
          fontFamily: FONT_MONO,
          letterSpacing: "0.05em",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        {QUALITY_LABEL[v.quality]}
      </div>
      <div
        style={{
          fontSize: 10,
          color: TEXT_DIM,
          fontFamily: FONT_MONO,
          marginBottom: 8,
        }}
      >
        {formula}
      </div>

      <MiniPiano
        midis={variantMidis}
        lowMidi={60}
        octaves={2}
        highlightColor={VIOLET_BRIGHT}
        preferFlats={v.root.includes("b")}
        className="block h-9 w-full"
      />

      <div
        style={{
          marginTop: 8,
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: TEXT_MUTE,
        }}
      >
        {noteLabels(v.noteNames).join(" · ")}
      </div>
    </button>
  );
}
